import { motion } from "framer-motion";

import { useEffect, useState } from "react";

import { useSmileSimulationController } from "../hooks/useSmileSimulationController";
import { getSmileSimulationCopy } from "../model/i18n";
import { SmileSimulationAuthLoadingPage } from "./SmileSimulationAuthLoadingPage";
import { SmileSimulationCapturePage } from "./SmileSimulationCapturePage";
import { SmileSimulationDownloadPreviewModal } from "./SmileSimulationDownloadPreviewModal";
import { SmileSimulationHistoryListPage } from "./SmileSimulationHistoryListPage";
import { SmileSimulationHomePage } from "./SmileSimulationHomePage";
import { SmileSimulationLoginPage } from "./SmileSimulationLoginPage";
import { SmileSimulationLogo } from "./SmileSimulationLogo";
import { SmileSimulationPatientDetailPage } from "./SmileSimulationPatientDetailPage";
import { SmileSimulationPatientInfoPage } from "./SmileSimulationPatientInfoPage";
import { SmileSimulationRecordDetailPage } from "./SmileSimulationRecordDetailPage";
import { SmileSimulationResultPage } from "./SmileSimulationResultPage";
import { SmileSimulationSettingsPanel } from "./SmileSimulationSettingsPanel";

type SmileSimulationScreenProps = {
  allowUnauthenticated?: boolean;
  isExperience?: boolean;
};

const AUTH_LOADING_TIMEOUT_MS = 10_000;

export function SmileSimulationScreen({
  allowUnauthenticated = false,
  isExperience = false,
}: SmileSimulationScreenProps) {
  const controller = useSmileSimulationController({ allowUnauthenticated });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAuthLoadingTimedOut, setIsAuthLoadingTimedOut] = useState(false);
  const copy = getSmileSimulationCopy(
    isExperience ? "ko" : controller.settings.language,
  );

  useEffect(() => {
    if (!controller.authGate.isLoading) {
      setIsAuthLoadingTimedOut(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsAuthLoadingTimedOut(true);
    }, AUTH_LOADING_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [controller.authGate.isLoading]);

  if (controller.authGate.isLoading) {
    return (
      <SmileSimulationAuthLoadingPage
        isTimedOut={isAuthLoadingTimedOut}
        onRefresh={() => window.location.reload()}
        onRetry={() => {
          setIsAuthLoadingTimedOut(false);
          void controller.authGate.retry();
        }}
      />
    );
  }

  const isLoginStep = controller.step === "login";
  const shouldShowHomeLogo =
    !isLoginStep &&
    (!isExperience ||
      controller.step === "history-list" ||
      controller.step === "simulation-detail");

  return (
    <main
      className={`relative h-screen overflow-hidden ${
        isLoginStep ? "bg-white" : "bg-[#f7f3ff]"
      }`}
      style={{ height: "100svh" }}
    >
      {shouldShowHomeLogo && (
        <div className="absolute inset-x-0 z-20 flex justify-center">
          <div
            className="relative w-[180px] md:w-[220px]"
            style={{ top: "max(1rem, env(safe-area-inset-top))" }}
          >
            <button
              type="button"
              onClick={controller.actions.goHome}
              aria-label={copy.home.goHome}
              className="relative z-10 block h-auto w-full"
            >
              <SmileSimulationLogo className="h-auto w-full" />
            </button>
          </div>
        </div>
      )}

      {!isLoginStep ? (
        <SmileSimulationSettingsPanel
          isOpen={isSettingsOpen}
          isExperience={isExperience}
          settings={controller.settings}
          onClose={() => setIsSettingsOpen(false)}
          onOpen={() => setIsSettingsOpen(true)}
          onLogout={controller.actions.logout}
          onUpdateLanguage={controller.actions.updateSimulationLanguage}
          onUpdatePromptVersion={
            controller.actions.updateSimulationPromptVersion
          }
        />
      ) : null}

      <h1 className="sr-only">Smile Simulation</h1>

      <div
        className={`relative z-10 flex h-full min-h-0 ${
          isLoginStep
            ? "items-center justify-center px-6 py-12 lg:px-8"
            : "items-center justify-center px-4 sm:px-6"
        }`}
        style={{
          paddingTop: isLoginStep
            ? "max(3rem, env(safe-area-inset-top))"
            : "max(1rem, env(safe-area-inset-top))",
          paddingBottom: isLoginStep
            ? "max(3rem, env(safe-area-inset-bottom))"
            : "max(1rem, env(safe-area-inset-bottom))",
        }}
      >
        <motion.div
          key={controller.step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`flex w-full flex-col items-center justify-center ${
            isLoginStep
              ? "max-w-sm"
              : isExperience
                ? `h-full min-h-0 max-w-[40rem] ${shouldShowHomeLogo ? "pt-16 sm:pt-20" : ""}`
                : "h-full min-h-0 max-w-[28rem] gap-4 pt-16 sm:gap-6 sm:pt-20"
          }`}
        >
          {controller.step === "login" ? (
            <SmileSimulationLoginPage
              auth={controller.auth}
              onEmailChange={controller.actions.updateEmail}
              onPasswordChange={controller.actions.updatePassword}
              onSubmit={controller.actions.submitLogin}
            />
          ) : controller.step === "home" ? (
            <SmileSimulationHomePage
              clinic={controller.clinic}
              home={controller.home}
              isExperience={isExperience}
              language={isExperience ? "ko" : controller.settings.language}
              onOpenPatient={controller.actions.openPatientDetail}
              onOpenHistory={controller.actions.openHistory}
              onStartNewSimulation={controller.actions.startNewSimulation}
            />
          ) : controller.step === "patient-intake" ? (
            <SmileSimulationPatientInfoPage
              clinic={controller.clinic}
              draft={controller.patient.draft}
              isExperience={isExperience}
              language={isExperience ? "ko" : controller.settings.language}
              onChartNumberChange={controller.actions.updatePatientChartNumber}
              onNameChange={controller.actions.updatePatientName}
              onSubmit={controller.actions.submitPatientInfo}
            />
          ) : controller.step === "capture" ? (
            controller.patient.current ? (
              <SmileSimulationCapturePage
                camera={controller.camera}
                capture={controller.capture}
                comparison={controller.comparison}
                isExperience={isExperience}
                language={isExperience ? "ko" : controller.settings.language}
                media={controller.media}
                onCapture={controller.actions.capture}
                onEditPatientInfo={controller.actions.editCurrentPatientInfo}
                onSwitchCamera={controller.actions.switchCamera}
                onUploadImage={controller.actions.uploadCaptureImage}
                patient={controller.patient.current}
                simulation={controller.simulation}
              />
            ) : null
          ) : (
            (() => {
              if (controller.step === "result") {
                return controller.patient.current ? (
                  <SmileSimulationResultPage
                    camera={controller.camera}
                    capture={controller.capture}
                    comparison={controller.comparison}
                    download={controller.download}
                    isExperience={isExperience}
                    language={
                      isExperience ? "ko" : controller.settings.language
                    }
                    media={controller.media}
                    onDownloadResultImage={
                      controller.actions.downloadCurrentResultImage
                    }
                    onOpenPatientDetail={
                      controller.actions.openCurrentPatientDetail
                    }
                    onRetake={controller.actions.retake}
                    onRetrySimulation={controller.actions.retry}
                    onStartSimulation={controller.actions.startSimulation}
                    patient={controller.patient.current}
                    simulation={controller.simulation}
                  />
                ) : null;
              }

              if (controller.step === "history-list") {
                return (
                  <SmileSimulationHistoryListPage
                    clinic={controller.clinic}
                    history={controller.history}
                    isExperience={isExperience}
                    language={
                      isExperience ? "ko" : controller.settings.language
                    }
                    onOpenPatient={controller.actions.openPatientDetail}
                    onQueryChange={controller.actions.updateHistoryQuery}
                    onStartNewSimulation={controller.actions.startNewSimulation}
                  />
                );
              }

              if (controller.step === "patient-detail") {
                return controller.history.selectedPatient ? (
                  <SmileSimulationPatientDetailPage
                    isExperience={isExperience}
                    language={
                      isExperience ? "ko" : controller.settings.language
                    }
                    onBack={controller.actions.goBackToHistoryList}
                    onOpenRecord={controller.actions.openRecordDetail}
                    onStartNewSimulation={controller.actions.startNewSimulation}
                    patient={controller.history.selectedPatient}
                  />
                ) : null;
              }

              if (controller.step === "simulation-detail") {
                return controller.history.selectedRecord ? (
                  <SmileSimulationRecordDetailPage
                    comparison={controller.comparison}
                    download={controller.download}
                    isExperience={isExperience}
                    language={
                      isExperience ? "ko" : controller.settings.language
                    }
                    onBack={
                      isExperience
                        ? controller.actions.goBackToHistoryList
                        : controller.actions.goBackToPatientDetail
                    }
                    onDownloadResultImage={
                      controller.actions.downloadSelectedRecordImage
                    }
                    record={controller.history.selectedRecord}
                  />
                ) : null;
              }

              return null;
            })()
          )}

          <canvas ref={controller.media.captureCanvasRef} className="hidden" />
        </motion.div>
      </div>

      {!isExperience ? (
        <SmileSimulationDownloadPreviewModal
          download={controller.download}
          language={controller.settings.language}
          onClose={controller.actions.closeDownloadPreview}
          onConfirm={controller.actions.confirmDownloadPreview}
          onToggleClinicWatermark={
            controller.actions.toggleDownloadPreviewClinicWatermark
          }
          onToggleServiceWatermark={
            controller.actions.toggleDownloadPreviewServiceWatermark
          }
          onUploadClinicWatermark={
            controller.actions.uploadDownloadPreviewClinicWatermark
          }
        />
      ) : null}
    </main>
  );
}
